import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Save, Plus, X, User, Briefcase, Building2, Phone, Mail, 
  Globe, MapPin, Link as LinkIcon, MessageSquare, Palette,
  Move, Trash2, Lock, Unlock, Settings, Eye,
  RefreshCw, ChevronDown, ChevronUp, GripVertical
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sample data for preview
const SAMPLE_DATA = {
  name: "John Anderson",
  designation: "Chief Executive Officer",
  company: "TechVision Inc.",
  phone: "+1 (555) 123-4567",
  email: "john@techvision.com",
  website: "www.techvision.com",
  location: "San Francisco, CA",
  bio: "Passionate tech leader with 15+ years of experience driving innovation.",
  profileImage: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face",
  socialLinks: [
    { platform: 'linkedin', url: '#' },
    { platform: 'twitter', url: '#' },
    { platform: 'instagram', url: '#' }
  ],
  extraLinks: [
    { title: 'Schedule Meeting', url: '#' }
  ]
};

// Phone dimensions - smaller for mobile
const PHONE_WIDTH = 320;
const PHONE_HEIGHT = 568;

// Default elements
const getDefaultElements = () => ({
  profileImage: { 
    id: 'profileImage', type: 'profileImage', 
    x: 110, y: 30, size: 100, 
    borderWidth: 4, borderColor: '#ffffff', borderRadius: '50%',
    shadow: '0 8px 24px rgba(0,0,0,0.15)', locked: false 
  },
  name: { 
    id: 'name', type: 'name', 
    x: 160, y: 145, 
    fontSize: '22px', fontWeight: 'bold', fontFamily: 'Inter, sans-serif',
    color: '#1f2937', textAlign: 'center', locked: false 
  },
  designation: { 
    id: 'designation', type: 'designation', 
    x: 160, y: 172, 
    fontSize: '13px', fontFamily: 'Inter, sans-serif',
    color: '#6b7280', textAlign: 'center', locked: false 
  },
  company: { 
    id: 'company', type: 'company', 
    x: 160, y: 190, 
    fontSize: '11px', fontFamily: 'Inter, sans-serif',
    color: '#9ca3af', textAlign: 'center', locked: false 
  },
  socialLinks: { 
    id: 'socialLinks', type: 'socialLinks', 
    x: 95, y: 215, 
    buttonSize: 40, iconSize: 18,
    backgroundColor: '#f3f4f6', borderRadius: '50%', 
    iconColor: '#374151', gap: 10, locked: false 
  },
  contactPhone: {
    id: 'contactPhone', type: 'contactItem', contactType: 'phone',
    x: 16, y: 275, width: 288,
    backgroundColor: '#f9fafb', borderRadius: '10px',
    iconColor: '#f59e0b', textColor: '#374151', fontSize: '13px', locked: false
  },
  contactEmail: {
    id: 'contactEmail', type: 'contactItem', contactType: 'email',
    x: 16, y: 325, width: 288,
    backgroundColor: '#f9fafb', borderRadius: '10px',
    iconColor: '#f59e0b', textColor: '#374151', fontSize: '13px', locked: false
  },
  bio: {
    id: 'bio', type: 'bio',
    x: 16, y: 375, width: 288,
    backgroundColor: '#fef3c7', borderRadius: '10px',
    textColor: '#92400e', fontSize: '12px', locked: false
  },
  extraLink: {
    id: 'extraLink', type: 'extraLink',
    x: 16, y: 455, width: 288,
    backgroundColor: '#f59e0b', borderRadius: '10px',
    textColor: '#ffffff', iconColor: '#ffffff', fontSize: '13px', locked: false
  }
});

// Color Input
const ColorInput = ({ value, onChange, label }) => (
  <div className="space-y-1">
    {label && <label className="text-xs text-gray-500">{label}</label>}
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded-lg cursor-pointer border-0 p-1"
      />
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg"
        placeholder="#000000"
      />
    </div>
  </div>
);

// Render element
const RenderElement = ({ element, isSelected, onClick, onDrag, scale = 1 }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const elementRef = useRef(null);

  // Handle both mouse and touch
  const handleStart = (clientX, clientY) => {
    if (element.locked) return;
    onClick(element.id);
    setIsDragging(true);
    setStartPos({
      x: clientX / scale - element.x,
      y: clientY / scale - element.y
    });
  };

  const handleMouseDown = (e) => {
    e.stopPropagation();
    handleStart(e.clientX, e.clientY);
  };

  const handleTouchStart = (e) => {
    e.stopPropagation();
    const touch = e.touches[0];
    handleStart(touch.clientX, touch.clientY);
  };

  useEffect(() => {
    const handleMove = (clientX, clientY) => {
      if (!isDragging) return;
      const newX = Math.max(0, Math.min(PHONE_WIDTH - 50, clientX / scale - startPos.x));
      const newY = Math.max(0, Math.min(PHONE_HEIGHT - 30, clientY / scale - startPos.y));
      onDrag(element.id, newX, newY);
    };

    const handleMouseMove = (e) => handleMove(e.clientX, e.clientY);
    const handleTouchMove = (e) => {
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    };
    const handleEnd = () => setIsDragging(false);

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, startPos, element.id, onDrag, scale]);

  const getContent = () => {
    switch (element.type) {
      case 'profileImage':
        return (
          <div
            style={{
              width: element.size || 100,
              height: element.size || 100,
              borderRadius: element.borderRadius || '50%',
              border: `${element.borderWidth || 4}px solid ${element.borderColor || '#fff'}`,
              boxShadow: element.shadow,
              backgroundImage: `url(${SAMPLE_DATA.profileImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
        );
      case 'name':
        return (
          <div style={{
            fontFamily: element.fontFamily,
            fontSize: element.fontSize || '22px',
            fontWeight: element.fontWeight || 'bold',
            color: element.color || '#1f2937',
            whiteSpace: 'nowrap'
          }}>
            {SAMPLE_DATA.name}
          </div>
        );
      case 'designation':
        return (
          <div style={{
            fontFamily: element.fontFamily,
            fontSize: element.fontSize || '13px',
            color: element.color || '#6b7280',
            whiteSpace: 'nowrap'
          }}>
            {SAMPLE_DATA.designation}
          </div>
        );
      case 'company':
        return (
          <div style={{
            fontFamily: element.fontFamily,
            fontSize: element.fontSize || '11px',
            color: element.color || '#9ca3af',
            whiteSpace: 'nowrap'
          }}>
            {SAMPLE_DATA.company}
          </div>
        );
      case 'socialLinks':
        return (
          <div className="flex" style={{ gap: element.gap || 10 }}>
            {SAMPLE_DATA.socialLinks.map((_, i) => (
              <div
                key={i}
                className="flex items-center justify-center"
                style={{
                  width: element.buttonSize || 40,
                  height: element.buttonSize || 40,
                  backgroundColor: element.backgroundColor || '#f3f4f6',
                  borderRadius: element.borderRadius || '50%'
                }}
              >
                <LinkIcon style={{ width: element.iconSize || 18, height: element.iconSize || 18, color: element.iconColor || '#374151' }} />
              </div>
            ))}
          </div>
        );
      case 'contactItem':
        const Icon = element.contactType === 'email' ? Mail : 
                    element.contactType === 'website' ? Globe :
                    element.contactType === 'location' ? MapPin : Phone;
        const value = element.contactType === 'email' ? SAMPLE_DATA.email :
                     element.contactType === 'website' ? SAMPLE_DATA.website :
                     element.contactType === 'location' ? SAMPLE_DATA.location : SAMPLE_DATA.phone;
        return (
          <div
            className="flex items-center gap-3 px-3 py-2.5"
            style={{
              width: element.width || 288,
              backgroundColor: element.backgroundColor || '#f9fafb',
              borderRadius: element.borderRadius || '10px'
            }}
          >
            <Icon style={{ width: 16, height: 16, color: element.iconColor || '#f59e0b', flexShrink: 0 }} />
            <span style={{ color: element.textColor || '#374151', fontSize: element.fontSize || '13px' }}>{value}</span>
          </div>
        );
      case 'bio':
        return (
          <div
            className="px-3 py-2.5"
            style={{
              width: element.width || 288,
              backgroundColor: element.backgroundColor || '#fef3c7',
              borderRadius: element.borderRadius || '10px'
            }}
          >
            <p style={{ color: element.textColor || '#92400e', fontSize: element.fontSize || '12px', lineHeight: 1.4 }}>
              {SAMPLE_DATA.bio}
            </p>
          </div>
        );
      case 'extraLink':
        return (
          <div
            className="flex items-center gap-3 px-3 py-2.5"
            style={{
              width: element.width || 288,
              backgroundColor: element.backgroundColor || '#f59e0b',
              borderRadius: element.borderRadius || '10px'
            }}
          >
            <LinkIcon style={{ width: 16, height: 16, color: element.iconColor || '#ffffff', flexShrink: 0 }} />
            <span style={{ color: element.textColor || '#ffffff', fontSize: element.fontSize || '13px', fontWeight: 500 }}>
              {SAMPLE_DATA.extraLinks[0].title}
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  const isCentered = ['name', 'designation', 'company'].includes(element.type);

  return (
    <div
      ref={elementRef}
      className={`absolute touch-none select-none ${element.locked ? 'cursor-not-allowed' : 'cursor-move'} ${
        isSelected ? 'ring-2 ring-amber-500 ring-offset-1 z-20' : 'z-10'
      }`}
      style={{
        left: element.x,
        top: element.y,
        transform: isCentered ? 'translateX(-50%)' : 'none'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {getContent()}
      {isSelected && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
          {element.locked ? <Lock className="w-3 h-3 text-white" /> : <Move className="w-3 h-3 text-white" />}
        </div>
      )}
    </div>
  );
};

// Properties Panel
const PropertiesPanel = ({ element, onChange, onDelete, onToggleLock, onClose }) => {
  if (!element) return null;

  const update = (key, value) => onChange({ ...element, [key]: value });

  return (
    <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl max-h-[70vh] md:max-h-[80vh] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
        <span className="font-semibold text-gray-800 capitalize">
          {element.type === 'contactItem' ? element.contactType : element.type.replace(/([A-Z])/g, ' $1')}
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => onToggleLock(element.id)}
            className={`p-2 rounded-lg ${element.locked ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-500'}`}
          >
            {element.locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
          </button>
          <button onClick={() => onDelete(element.id)} className="p-2 rounded-lg bg-red-50 text-red-500">
            <Trash2 className="w-4 h-4" />
          </button>
          <button onClick={onClose} className="p-2 rounded-lg bg-gray-100 text-gray-500 md:hidden">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 overflow-y-auto flex-1">
        {/* Position */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">X Position</label>
            <input
              type="number"
              value={Math.round(element.x)}
              onChange={(e) => update('x', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-base"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Y Position</label>
            <input
              type="number"
              value={Math.round(element.y)}
              onChange={(e) => update('y', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-base"
            />
          </div>
        </div>

        {/* Profile Image */}
        {element.type === 'profileImage' && (
          <>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Size (px)</label>
              <input
                type="number"
                value={element.size || 100}
                onChange={(e) => update('size', parseInt(e.target.value) || 100)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-base"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Border</label>
                <input
                  type="number"
                  value={element.borderWidth || 4}
                  onChange={(e) => update('borderWidth', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-base"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Radius</label>
                <input
                  type="text"
                  value={element.borderRadius || '50%'}
                  onChange={(e) => update('borderRadius', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-base"
                />
              </div>
            </div>
            <ColorInput label="Border Color" value={element.borderColor} onChange={(v) => update('borderColor', v)} />
          </>
        )}

        {/* Text Elements */}
        {['name', 'designation', 'company'].includes(element.type) && (
          <>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Font</label>
              <select
                value={element.fontFamily || 'Inter, sans-serif'}
                onChange={(e) => update('fontFamily', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-base"
              >
                <option value="Inter, sans-serif">Inter</option>
                <option value="Playfair Display, serif">Playfair Display</option>
                <option value="Poppins, sans-serif">Poppins</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Size</label>
                <input
                  type="text"
                  value={element.fontSize || '16px'}
                  onChange={(e) => update('fontSize', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-base"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Weight</label>
                <select
                  value={element.fontWeight || 'normal'}
                  onChange={(e) => update('fontWeight', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-base"
                >
                  <option value="normal">Normal</option>
                  <option value="500">Medium</option>
                  <option value="bold">Bold</option>
                </select>
              </div>
            </div>
            <ColorInput label="Color" value={element.color} onChange={(v) => update('color', v)} />
          </>
        )}

        {/* Social Links */}
        {element.type === 'socialLinks' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Button Size</label>
                <input
                  type="number"
                  value={element.buttonSize || 40}
                  onChange={(e) => update('buttonSize', parseInt(e.target.value) || 40)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-base"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Gap</label>
                <input
                  type="number"
                  value={element.gap || 10}
                  onChange={(e) => update('gap', parseInt(e.target.value) || 10)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-base"
                />
              </div>
            </div>
            <ColorInput label="Background" value={element.backgroundColor} onChange={(v) => update('backgroundColor', v)} />
            <ColorInput label="Icon Color" value={element.iconColor} onChange={(v) => update('iconColor', v)} />
          </>
        )}

        {/* Contact, Bio, Link */}
        {['contactItem', 'bio', 'extraLink'].includes(element.type) && (
          <>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Width</label>
              <input
                type="number"
                value={element.width || 288}
                onChange={(e) => update('width', parseInt(e.target.value) || 288)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-base"
              />
            </div>
            <ColorInput label="Background" value={element.backgroundColor} onChange={(v) => update('backgroundColor', v)} />
            <ColorInput label="Text Color" value={element.textColor} onChange={(v) => update('textColor', v)} />
            {element.type !== 'bio' && (
              <ColorInput label="Icon Color" value={element.iconColor} onChange={(v) => update('iconColor', v)} />
            )}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Border Radius</label>
              <input
                type="text"
                value={element.borderRadius || '10px'}
                onChange={(e) => update('borderRadius', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-base"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Editor Modal
const TemplateEditorModal = ({ template, onSave, onClose }) => {
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showBgSettings, setShowBgSettings] = useState(false);
  const [showProperties, setShowProperties] = useState(false);
  const { getIdToken } = useAuth();

  useEffect(() => {
    if (template) {
      setEditingTemplate({
        ...template,
        elements: template.elements || getDefaultElements()
      });
    }
  }, [template]);

  const updateElement = (id, updates) => {
    setEditingTemplate(prev => ({
      ...prev,
      elements: { ...prev.elements, [id]: { ...prev.elements[id], ...updates } }
    }));
  };

  const handleDrag = (id, x, y) => updateElement(id, { x, y });

  const deleteElement = (id) => {
    setEditingTemplate(prev => {
      const newElements = { ...prev.elements };
      delete newElements[id];
      return { ...prev, elements: newElements };
    });
    setSelectedElementId(null);
    setShowProperties(false);
  };

  const toggleLock = (id) => {
    setEditingTemplate(prev => ({
      ...prev,
      elements: { ...prev.elements, [id]: { ...prev.elements[id], locked: !prev.elements[id].locked } }
    }));
  };

  const addElement = (type, contactType = null) => {
    const id = `${type}_${Date.now()}`;
    const y = Object.keys(editingTemplate.elements).length * 50 + 280;
    let newElement = { id, type, x: 16, y, locked: false };

    if (type === 'contactItem') {
      newElement = { ...newElement, contactType: contactType || 'phone', width: 288, backgroundColor: '#f9fafb', borderRadius: '10px', iconColor: '#f59e0b', textColor: '#374151' };
    } else if (type === 'bio') {
      newElement = { ...newElement, width: 288, backgroundColor: '#fef3c7', borderRadius: '10px', textColor: '#92400e' };
    } else if (type === 'extraLink') {
      newElement = { ...newElement, width: 288, backgroundColor: '#f59e0b', borderRadius: '10px', textColor: '#ffffff', iconColor: '#ffffff' };
    }

    setEditingTemplate(prev => ({ ...prev, elements: { ...prev.elements, [id]: newElement } }));
    setSelectedElementId(id);
    setShowProperties(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_URL}/api/bio-templates/admin/${editingTemplate.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editingTemplate)
      });
      if (response.ok) {
        const updated = await response.json();
        onSave(updated);
      }
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleElementClick = (id) => {
    setSelectedElementId(id);
    setShowProperties(true);
  };

  if (!editingTemplate) return null;

  const selectedElement = selectedElementId ? editingTemplate.elements[selectedElementId] : null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex flex-col md:items-center md:justify-center">
      {/* Header - Fixed */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
          <span className="font-semibold text-gray-800 truncate max-w-[150px] md:max-w-none">
            {editingTemplate.name}
          </span>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium flex items-center gap-2 text-sm"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          <span className="hidden sm:inline">Save</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex flex-col md:flex-row md:max-w-6xl md:w-full md:max-h-[85vh] md:m-4 md:rounded-2xl md:bg-white">
        {/* Phone Preview Area */}
        <div className="flex-1 bg-gray-100 overflow-y-auto p-4 flex flex-col items-center">
          {/* Background Settings Toggle */}
          <button
            onClick={() => setShowBgSettings(!showBgSettings)}
            className="mb-3 px-4 py-2 bg-white rounded-xl shadow-sm flex items-center gap-2 text-sm font-medium"
          >
            <Palette className="w-4 h-4 text-amber-500" />
            Background
            {showBgSettings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showBgSettings && (
            <div className="mb-4 p-4 bg-white rounded-xl shadow-sm w-full max-w-sm space-y-3">
              <ColorInput 
                label="Page Background" 
                value={editingTemplate.pageBackgroundColor} 
                onChange={(v) => setEditingTemplate(prev => ({ ...prev, pageBackgroundColor: v }))} 
              />
              <div>
                <label className="text-xs text-gray-500">Gradient</label>
                <input
                  type="text"
                  value={editingTemplate.pageBackgroundGradient || ''}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, pageBackgroundGradient: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
                  placeholder="linear-gradient(...)"
                />
              </div>
              <ColorInput 
                label="Accent Color" 
                value={editingTemplate.accentColor} 
                onChange={(v) => setEditingTemplate(prev => ({ ...prev, accentColor: v }))} 
              />
            </div>
          )}

          {/* Phone Frame */}
          <div className="bg-gray-800 rounded-[2rem] p-2 shadow-2xl">
            <div className="relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-4 bg-gray-800 rounded-b-xl z-30" />
            </div>
            <div
              className="relative rounded-[1.5rem] overflow-hidden touch-none"
              style={{
                width: PHONE_WIDTH,
                height: PHONE_HEIGHT,
                background: editingTemplate.pageBackgroundGradient || editingTemplate.pageBackgroundColor || '#f9fafb'
              }}
              onClick={() => { setSelectedElementId(null); setShowProperties(false); }}
            >
              {/* Header bar */}
              {editingTemplate.header?.height && editingTemplate.header.height !== '0px' && (
                <div
                  className="absolute top-0 left-0 right-0"
                  style={{
                    height: editingTemplate.header.height,
                    background: editingTemplate.header.backgroundColor || editingTemplate.accentColor || '#f59e0b'
                  }}
                />
              )}

              {/* Elements */}
              {Object.values(editingTemplate.elements).map((el) => (
                <RenderElement
                  key={el.id}
                  element={el}
                  isSelected={selectedElementId === el.id}
                  onClick={handleElementClick}
                  onDrag={handleDrag}
                />
              ))}
            </div>
            <div className="flex justify-center mt-1">
              <div className="w-24 h-1 bg-gray-600 rounded-full" />
            </div>
          </div>

          {/* Add Elements */}
          <div className="mt-4 p-3 bg-white rounded-xl shadow-sm">
            <p className="text-xs text-gray-500 text-center mb-2">Add Elements</p>
            <div className="flex flex-wrap gap-2 justify-center">
              <button onClick={() => addElement('contactItem', 'phone')} className="px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5" /> Phone
              </button>
              <button onClick={() => addElement('contactItem', 'email')} className="px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> Email
              </button>
              <button onClick={() => addElement('contactItem', 'website')} className="px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" /> Web
              </button>
              <button onClick={() => addElement('bio')} className="px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" /> Bio
              </button>
              <button onClick={() => addElement('extraLink')} className="px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1.5">
                <LinkIcon className="w-3.5 h-3.5" /> Link
              </button>
            </div>
          </div>

          {/* Elements List */}
          <div className="mt-3 w-full max-w-sm bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b text-xs font-medium text-gray-600">
              Elements ({Object.keys(editingTemplate.elements).length})
            </div>
            <div className="max-h-32 overflow-y-auto">
              {Object.values(editingTemplate.elements).map((el) => (
                <button
                  key={el.id}
                  onClick={() => handleElementClick(el.id)}
                  className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between ${
                    selectedElementId === el.id ? 'bg-amber-50 text-amber-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <span className="capitalize text-xs">
                    {el.type === 'contactItem' ? el.contactType : el.type.replace(/([A-Z])/g, ' $1')}
                  </span>
                  {el.locked && <Lock className="w-3 h-3 text-gray-400" />}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Properties Panel - Desktop: Side, Mobile: Bottom Sheet */}
        {showProperties && selectedElement && (
          <>
            {/* Desktop */}
            <div className="hidden md:block w-72 border-l bg-white overflow-y-auto">
              <PropertiesPanel
                element={selectedElement}
                onChange={(updated) => updateElement(selectedElementId, updated)}
                onDelete={deleteElement}
                onToggleLock={toggleLock}
                onClose={() => setShowProperties(false)}
              />
            </div>
            {/* Mobile Bottom Sheet */}
            <div className="md:hidden fixed inset-x-0 bottom-0 z-50">
              <div 
                className="absolute inset-0 -top-full bg-black/30"
                onClick={() => setShowProperties(false)}
              />
              <PropertiesPanel
                element={selectedElement}
                onChange={(updated) => updateElement(selectedElementId, updated)}
                onDelete={deleteElement}
                onToggleLock={toggleLock}
                onClose={() => setShowProperties(false)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Template Card
const TemplateCard = ({ template, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white rounded-xl shadow hover:shadow-lg transition-all cursor-pointer overflow-hidden border border-gray-100 hover:border-amber-300 active:scale-[0.98]"
  >
    <div 
      className="h-32 sm:h-40 relative"
      style={{ background: template.pageBackgroundGradient || template.pageBackgroundColor || '#f9fafb' }}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-10 h-10 rounded-full bg-white/60 shadow" />
      </div>
      <div className="absolute top-2 right-2">
        <div className="px-2 py-1 bg-white/90 backdrop-blur text-xs rounded-lg flex items-center gap-1 text-amber-600 font-medium">
          <Eye className="w-3 h-3" /> Edit
        </div>
      </div>
    </div>
    <div className="p-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800 text-sm">{template.name}</h3>
        {template.isActive !== false ? (
          <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] rounded-full">Active</span>
        ) : (
          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded-full">Inactive</span>
        )}
      </div>
    </div>
  </div>
);

// Main Component
const AdminBioTemplates = () => {
  const { getIdToken } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/bio-templates`);
      const data = await response.json();
      setTemplates(data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load templates' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const seedDefaults = async () => {
    try {
      const token = await getIdToken();
      await fetch(`${API_URL}/api/bio-templates/admin/seed-defaults`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Templates seeded!' });
      fetchTemplates();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to seed' });
    }
  };

  const createTemplate = async () => {
    const name = prompt('Template name:');
    if (!name) return;
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_URL}/api/bio-templates/admin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, pageBackgroundColor: '#f9fafb', accentColor: '#f59e0b',
          elements: getDefaultElements(), isActive: true
        })
      });
      if (response.ok) {
        const newTemplate = await response.json();
        setTemplates(prev => [...prev, newTemplate]);
        setSelectedTemplate(newTemplate);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to create' });
    }
  };

  const handleSave = (updated) => {
    setTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
    setSelectedTemplate(null);
    setMessage({ type: 'success', text: 'Saved!' });
    setTimeout(() => setMessage(null), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Bio Templates</h2>
          <p className="text-sm text-gray-500">Tap to edit</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={seedDefaults}
            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center gap-2 text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Seed</span>
          </button>
          <button
            onClick={createTemplate}
            className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl flex items-center gap-2 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            New
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-xl flex items-center justify-between text-sm ${
          message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Templates Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {templates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            onClick={() => setSelectedTemplate(template)}
          />
        ))}
      </div>

      {/* Editor Modal */}
      {selectedTemplate && (
        <TemplateEditorModal
          template={selectedTemplate}
          onSave={handleSave}
          onClose={() => setSelectedTemplate(null)}
        />
      )}
    </div>
  );
};

export default AdminBioTemplates;
